import { useEffect, useRef, useState } from 'react';
import { api } from '../services/api';
import { incidentsApi } from '../services/incidentsApi';
import { OVERVIEW_EVIDENCE_THUMB_TIMEOUT_MS } from '../domain/continuousSafety/overviewConditionEvidenceThumb';
import {
  incidentEvidenceTimestamp,
  incidentMatchesBoundIdentity,
  incidentWithinEvidenceInterval,
  pickUniqueRecentEvidenceIncidents,
  SHOWCASE_PROX_EVIDENCE_CAMERA_ID,
  SHOWCASE_PROX_EVIDENCE_CONDITION_ID,
  SHOWCASE_PROX_EVIDENCE_INCIDENT_TYPE,
  SHOWCASE_RECENT_EVIDENCE_LIST_LIMIT,
  SHOWCASE_RECENT_EVIDENCE_LIST_PAGES,
  SHOWCASE_RECENT_EVIDENCE_LIST_TIMEOUT_MS,
  SHOWCASE_RECENT_EVIDENCE_MAX_ITEMS,
  type ShowcaseRecentEvidenceItem,
  type ShowcaseRecentEvidenceState,
} from '../domain/showcase/showcaseRecentEvidence';

function utcQueryInstant(value: string | null | undefined): string | null {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const ms = Date.parse(raw);
  if (!Number.isFinite(ms)) return null;
  return new Date(ms).toISOString();
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      reject(new Error('showcase_recent_evidence_timeout'));
    }, ms);
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        window.clearTimeout(timer);
        reject(error);
      },
    );
  });
}

async function fetchLatestSnapshotObjectUrl(incidentId: string): Promise<string> {
  const res = await api.get(
    `/api/v1/incidents/${encodeURIComponent(incidentId)}/evidence/latest/snapshot`,
    { responseType: 'blob' },
  );
  const blob = res.data as Blob;
  if (!blob || blob.size < 32) {
    throw new Error('showcase_recent_evidence_empty_blob');
  }
  if (blob.type && !String(blob.type).startsWith('image/')) {
    throw new Error(`showcase_recent_evidence_non_image:${blob.type}`);
  }
  return URL.createObjectURL(blob);
}

/**
 * Read-only recent stills for Showcase Room B conditions.
 * GET listIncidents + GET latest snapshot only. Never invents fixtures.
 */
export function useShowcaseRecentEvidence(params: {
  enabled?: boolean;
  siteId?: string | null;
  cameraId?: string | null;
  conditionId?: string | null;
  policyId?: string | null;
  zoneId?: string | null;
  incidentType?: string | null;
  candidateIncidentTypes?: string[] | null;
  /** Inclusive UTC start of the selected peak. Required; recent evidence is not a fallback. */
  occurredFrom?: string | null;
  /** Exclusive UTC end of the selected peak. */
  occurredTo?: string | null;
}): ShowcaseRecentEvidenceState {
  const enabled = params.enabled !== false;
  const siteId = String(params.siteId || '').trim();
  const cameraId = String(params.cameraId || SHOWCASE_PROX_EVIDENCE_CAMERA_ID).trim()
    || SHOWCASE_PROX_EVIDENCE_CAMERA_ID;
  const conditionId = String(params.conditionId || SHOWCASE_PROX_EVIDENCE_CONDITION_ID).trim()
    || SHOWCASE_PROX_EVIDENCE_CONDITION_ID;
  const policyId = String(params.policyId || '').trim();
  const zoneId = String(params.zoneId || '').trim();
  const incidentType = String(params.incidentType || SHOWCASE_PROX_EVIDENCE_INCIDENT_TYPE).trim().toLowerCase()
    || SHOWCASE_PROX_EVIDENCE_INCIDENT_TYPE;
  const occurredFrom = utcQueryInstant(params.occurredFrom);
  const occurredTo = utcQueryInstant(params.occurredTo);
  const candidateKey = (params.candidateIncidentTypes || [])
    .map((value) => String(value || '').trim().toLowerCase())
    .filter(Boolean)
    .join('|');
  const [state, setState] = useState<ShowcaseRecentEvidenceState>({ status: 'idle', items: [] });
  const objectUrlsRef = useRef<Set<string>>(new Set());
  const generationRef = useRef(0);
  const scopeKeyRef = useRef('');

  useEffect(() => {
    const generation = generationRef.current + 1;
    generationRef.current = generation;

    const revokeAll = () => {
      Array.from(objectUrlsRef.current).forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch {
          // ignore
        }
      });
      objectUrlsRef.current.clear();
    };

    const scopeKey = [
      siteId, cameraId, conditionId, policyId, zoneId, incidentType, occurredFrom, occurredTo,
    ].join('|');
    const scopeChanged = scopeKeyRef.current !== scopeKey;
    scopeKeyRef.current = scopeKey;

    if (!enabled || !siteId || !cameraId || !occurredFrom || !occurredTo) {
      revokeAll();
      setState({ status: !enabled || !siteId || !cameraId ? 'idle' : 'empty', items: [] });
      return () => {
        generationRef.current += 1;
      };
    }

    setState((current) => (
      !scopeChanged && current.items.length > 0
        ? current
        : { status: 'loading', items: [] }
    ));

    void (async () => {
      const empty: ShowcaseRecentEvidenceState = { status: 'empty', items: [] };
      try {
        const listed: Awaited<ReturnType<typeof incidentsApi.listIncidents>>['incidents'] = [];
        const seenIds = new Set<string>();
        for (let page = 0; page < SHOWCASE_RECENT_EVIDENCE_LIST_PAGES; page += 1) {
          if (generation !== generationRef.current) return;
          const { incidents } = await withTimeout(
            incidentsApi.listIncidents(
              undefined,
              undefined,
              SHOWCASE_RECENT_EVIDENCE_LIST_LIMIT,
              page * SHOWCASE_RECENT_EVIDENCE_LIST_LIMIT,
              undefined,
              undefined,
              incidentType,
              zoneId || undefined,
              undefined,
              cameraId,
              {
                skip_total: true,
                condition_id: conditionId,
                policy_id: policyId || undefined,
                occurred_from: occurredFrom,
                occurred_to: occurredTo,
              },
            ),
            SHOWCASE_RECENT_EVIDENCE_LIST_TIMEOUT_MS,
          );
          for (const incident of incidents || []) {
            const id = String(incident.incident_id || '').trim();
            if (!id || seenIds.has(id)) continue;
            if (!incidentWithinEvidenceInterval(incident, { start: occurredFrom, end: occurredTo })) continue;
            if (!incidentMatchesBoundIdentity(incident, { conditionId, policyId })) continue;
            seenIds.add(id);
            listed.push(incident);
          }
          const readySoFar = pickUniqueRecentEvidenceIncidents(listed, {
            cameraId,
            zoneId,
            incidentType,
            candidateIncidentTypes: candidateKey ? candidateKey.split('|') : undefined,
          }, SHOWCASE_RECENT_EVIDENCE_MAX_ITEMS);
          if (readySoFar.length >= SHOWCASE_RECENT_EVIDENCE_MAX_ITEMS) break;
          if ((incidents || []).length < SHOWCASE_RECENT_EVIDENCE_LIST_LIMIT) break;
        }
        if (generation !== generationRef.current) return;

        const candidates = pickUniqueRecentEvidenceIncidents(listed, {
          cameraId,
          zoneId,
          incidentType,
          candidateIncidentTypes: candidateKey ? candidateKey.split('|') : undefined,
        }, SHOWCASE_RECENT_EVIDENCE_MAX_ITEMS * 3);

        const items: ShowcaseRecentEvidenceItem[] = [];
        for (const candidate of candidates) {
          if (generation !== generationRef.current) return;
          if (items.length >= SHOWCASE_RECENT_EVIDENCE_MAX_ITEMS) break;
          const incidentId = String(candidate.incident_id || '').trim();
          if (!incidentId) continue;
          try {
            const imageUrl = await withTimeout(
              fetchLatestSnapshotObjectUrl(incidentId),
              Math.min(OVERVIEW_EVIDENCE_THUMB_TIMEOUT_MS, 8_000),
            );
            if (generation !== generationRef.current) {
              try {
                URL.revokeObjectURL(imageUrl);
              } catch {
                // ignore
              }
              return;
            }
            objectUrlsRef.current.add(imageUrl);
            items.push({
              incidentId,
              timestamp: incidentEvidenceTimestamp(candidate),
              imageUrl,
              status: 'ready',
            });
          } catch {
            // Try the next AVAILABLE/READY incident. Evidence failure is not a condition failure.
          }
        }

        if (generation !== generationRef.current) return;
        if (items.length > 0) {
          const keep = new Set(items.map((item) => item.imageUrl));
          Array.from(objectUrlsRef.current).forEach((url) => {
            if (keep.has(url)) return;
            try {
              URL.revokeObjectURL(url);
            } catch {
              // ignore
            }
            objectUrlsRef.current.delete(url);
          });
          setState({ status: 'ready', items });
          return;
        }
        setState(empty);
      } catch {
        if (generation !== generationRef.current) return;
        setState((current) => (
          !scopeChanged && current.items.length > 0 ? current : empty
        ));
      }
    })();

    return () => {
      generationRef.current += 1;
    };
  }, [enabled, siteId, cameraId, conditionId, policyId, zoneId, incidentType, candidateKey, occurredFrom, occurredTo]);

  useEffect(() => () => {
    Array.from(objectUrlsRef.current).forEach((url) => {
      try {
        URL.revokeObjectURL(url);
      } catch {
        // ignore
      }
    });
    objectUrlsRef.current.clear();
  }, []);

  return state;
}
